import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { type ChainId, useTokenBalance } from '@repo/web3';
import { useQueryClient } from '@tanstack/react-query';
import { useWeb3Context } from '@/contexts/Web3Context';

/**
 * Reads the user's ERC-20 balance on `chainId` regardless of the wallet chain.
 * `staleTime: Infinity` means it only rereads on `refresh`; `gcTime: 0` drops an unobserved balance
 * so returning to an account rereads it.
 */
export const useUserTokenHolding = (tokenAddress, chainId) => {
  const $ = _c(14);
  const {
    account
  } = useWeb3Context();
  const queryClient = useQueryClient();
  const t0 = account?.address;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      staleTime: Infinity,
      gcTime: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== chainId || $[2] !== t0 || $[3] !== tokenAddress) {
    t2 = {
      chainId,
      userAddress: t0,
      tokenAddress,
      query: t1
    };
    $[1] = chainId;
    $[2] = t0;
    $[3] = tokenAddress;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data,
    isFetching,
    refetch,
    queryKey
  } = useTokenBalance(t2);
  let t3;
  if ($[5] !== account?.address || $[6] !== queryClient || $[7] !== queryKey || $[8] !== refetch) {
    t3 = t4 => {
      const hard = t4 === undefined ? false : t4;
      if (!account?.address) {
        return;
      }
      if (hard) {
        queryClient.resetQueries({
          queryKey
        });
      } else {
        refetch();
      }
    };
    $[5] = account?.address;
    $[6] = queryClient;
    $[7] = queryKey;
    $[8] = refetch;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== data || $[11] !== isFetching || $[12] !== t3) {
    t4 = {
      value: data,
      loading: isFetching,
      refresh: t3
    };
    $[10] = data;
    $[11] = isFetching;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};